import React, { useState, useEffect, useMemo, useRef } from 'react'
import * as S from './styles'

import DndListHandle from '../Mantine/Dragndrop'
import Input from '../Mantine/Input'
import Icone from '../Icone'
import { useGlobal } from '../../stores/useGlobal'

const GROUP_ID_ACTIVE = 'active-quick-buttons'
const GROUP_ID_AVAILABLE = 'available-quick-buttons'
const MAX_ACTIVE_ITEMS = 5

const EmptyStateDroppable = () => {
  return (
    <S.EmptyStateCard>
      <Icone nome="eye" color="default" width="24px" height="24px" />
      <S.EmptyStateText>
        Personalize para acessar suas ações de atalho com mais rapidez.
      </S.EmptyStateText>
    </S.EmptyStateCard>
  )
}

const EmptyFilteredAvailableState = ({
  temItensDisponiveis,
  temBuscaAtiva
}) => {
  const mensagem = temItensDisponiveis
    ? 'Nenhuma ação encontrada para esse filtro.'
    : 'Todas as ações já foram favoritadas.'

  return <S.EmptyFilteredText>{mensagem}</S.EmptyFilteredText>
}

/**
 * @param {Object} props
 * @param {Array} props.acoesData
 * @param {Array} props.favoritasData
 * @param {string} props.manejador
 * @param {boolean} [props.isOpen]
 */
const QuickButtons = React.forwardRef(
  ({ acoesData = [], favoritasData = [], manejador, isOpen = true }, ref) => {
    const { setMostrarNotificacao } = useGlobal()
    const [activeButtons, setActiveButtons] = useState([])
    const [availableButtons, setAvailableButtons] = useState([])
    const orderAfterDragRef = useRef(null)
    const prevIsOpenRef = useRef(false)
    const hideAllTimeoutRef = useRef(null)

    useEffect(() => {
      return () => {
        if (hideAllTimeoutRef.current) clearTimeout(hideAllTimeoutRef.current)
      }
    }, [])

    useEffect(() => {
      const modalAcabouDeAbrir = isOpen && !prevIsOpenRef.current
      prevIsOpenRef.current = isOpen

      if (!modalAcabouDeAbrir) return

      const favoritasFormatadas = favoritasData
        .map((fav, index) => ({
          ...fav,
          id:
            fav.cd_pav_acao ||
            fav.id ||
            fav.ds_acao_redux ||
            `favorita-${manejador}-${index}`,
          name: fav.ds_nome || fav.ds_acao || 'Ação sem nome'
        }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

      const acoesFormatadas = acoesData.map((acao, index) => ({
        ...acao,
        id:
          acao.cd_pav_acao ||
          acao.id ||
          acao.ds_acao_redux ||
          `acao-${manejador}-${index}`,
        name: acao.ds_nome || acao.ds_acao || 'Ação sem nome'
      }))

      setActiveButtons(favoritasFormatadas)
      setAvailableButtons(acoesFormatadas)
      orderAfterDragRef.current = favoritasFormatadas
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, acoesData, favoritasData, manejador])

    const handleSalvarFavoritas = async () => {
      try {
        const favoritosNaOrdemDoDrag =
          orderAfterDragRef.current ?? activeButtons
        const todosDadosOriginais = [...favoritasData, ...acoesData]
        const favoritasAtualizadas = favoritosNaOrdemDoDrag.map(
          (btn, index) => {
            const itemOriginal = todosDadosOriginais.find(
              (item) =>
                String(item.cd_pav_acao || item.id || item.ds_acao_redux) ===
                String(btn.id)
            )
            const base = itemOriginal
              ? { ...itemOriginal }
              : {
                  cd_pav_acao: btn.id,
                  ds_acao: btn.name,
                  ds_acao_redux: btn.ds_acao_redux || '',
                  fl_relatorio: 0,
                  fl_selecionado: 0
                }
            return { ...base, order: index }
          }
        )
        const acoesAtualizadas = availableButtons.map((btn) => {
          const itemOriginal = todosDadosOriginais.find(
            (item) =>
              (item.cd_pav_acao || item.id || item.ds_acao_redux) === btn.id
          )

          if (itemOriginal) return { ...itemOriginal }

          return {
            cd_pav_acao: btn.id,
            ds_acao: btn.name,
            ds_acao_redux: btn.ds_acao_redux || '',
            fl_relatorio: 0,
            fl_selecionado: 0
          }
        })
        return {
          sucesso: true,
          favoritasAtualizadas,
          acoesAtualizadas
        }
      } catch (error) {
        return { sucesso: false }
      }
    }

    // Expõe a função de salvar via ref
    React.useImperativeHandle(ref, () => ({
      salvar: handleSalvarFavoritas
    }))
    const [isHidingAll, setIsHidingAll] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

    useEffect(() => {
      if (searchTerm === '') {
        setDebouncedSearchTerm('')
        return
      }

      const handler = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm)
      }, 500)

      return () => {
        clearTimeout(handler)
      }
    }, [searchTerm])

    const filteredAvailableButtons = useMemo(
      () =>
        availableButtons.filter((item) =>
          item.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        ),
      [availableButtons, debouncedSearchTerm]
    )

    const handleOcultarTodos = () => {
      if (activeButtons.length === 0) return
      if (hideAllTimeoutRef.current) clearTimeout(hideAllTimeoutRef.current)
      setIsHidingAll(true)
      hideAllTimeoutRef.current = setTimeout(() => {
        hideAllTimeoutRef.current = null
        setAvailableButtons((prev) => [...activeButtons, ...prev])
        setActiveButtons([])
        orderAfterDragRef.current = []
        setIsHidingAll(false)
      }, 300)
    }

    const handleMostrarTodos = () => {
      const totalAposAdicionar =
        activeButtons.length + filteredAvailableButtons.length

      if (totalAposAdicionar > MAX_ACTIVE_ITEMS) {
        setMostrarNotificacao({
          show: true,
          type: 'warning',
          title: 'Limite atingido',
          message: `Não é possível mostrar todos. Você pode selecionar no máximo ${MAX_ACTIVE_ITEMS} botões.`
        })
        return
      }

      const idsParaMover = filteredAvailableButtons.map((btn) => btn.id)
      setActiveButtons((prev) => {
        const next = [...prev, ...filteredAvailableButtons]
        orderAfterDragRef.current = next
        return next
      })
      setAvailableButtons((prev) =>
        prev.filter((btn) => !idsParaMover.includes(btn.id))
      )
    }

    const handleOrderChange = (newOrder) => {
      orderAfterDragRef.current = newOrder
      setActiveButtons(newOrder)
    }

    const handleIconClick = (itemId) => {
      const item =
        activeButtons.find((i) => i.id === itemId) ||
        availableButtons.find((i) => i.id === itemId)

      if (!item) {
        return
      }

      const isInActive =
        activeButtons.find((i) => i.id === itemId) !== undefined

      if (isInActive) {
        setActiveButtons((prev) => {
          const next = prev.filter((i) => i.id !== itemId)
          orderAfterDragRef.current = next
          return next
        })
        setAvailableButtons((prev) => [item, ...prev])
      } else {
        if (activeButtons.length >= MAX_ACTIVE_ITEMS) {
          setMostrarNotificacao({
            show: true,
            type: 'warning',
            title: 'Limite atingido',
            message: `Você pode selecionar no máximo ${MAX_ACTIVE_ITEMS} botões para exibir no atalho.`
          })
          return
        }

        setAvailableButtons((prev) => prev.filter((i) => i.id !== itemId))
        setActiveButtons((prev) => {
          const next = [item, ...prev]
          orderAfterDragRef.current = next
          return next
        })
      }
    }

    return (
      <S.QuickButtonsContainer>
        <S.Card>
          <S.Header>
            <S.Title>
              Selecione até no máximo 5 botões que serão exibidos na barra de
              atalho página de Dispositivos.
            </S.Title>

            <Input
              label="Ações"
              placeholder="Pesquisar por ação"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </S.Header>

          <S.SectionTransition $isTransitioning={isHidingAll}>
            <S.TitleWithButton>
              <S.TextWrapper>
                <S.Title>Mostrado no atalho</S.Title>
                <S.Description>
                  Clique no ícone para adicionar e organize conforme sua
                  preferência.
                </S.Description>
              </S.TextWrapper>
              {activeButtons.length > 0 && (
                <S.SubtleButton
                  label="Ocultar todos"
                  variant="subtle"
                  uppercase={false}
                  onClick={handleOcultarTodos}
                  disabled={isHidingAll}
                />
              )}
            </S.TitleWithButton>
            {activeButtons.length > 0 ? (
              <DndListHandle
                data={activeButtons}
                icon="eye-off"
                onIconClick={(itemId) =>
                  handleIconClick(itemId, GROUP_ID_ACTIVE)
                }
                onOrderChange={handleOrderChange}
              />
            ) : (
              <EmptyStateDroppable />
            )}
          </S.SectionTransition>
          <S.TitleWithButton>
            <S.Title>Oculto no atalho</S.Title>
            {filteredAvailableButtons.length > 0 && (
              <S.SubtleButton
                label="Mostrar todos"
                variant="subtle"
                uppercase={false}
                onClick={handleMostrarTodos}
              />
            )}
          </S.TitleWithButton>
          {filteredAvailableButtons.length > 0 ? (
            <DndListHandle
              data={filteredAvailableButtons}
              icon="eye"
              draggable={false}
              onIconClick={(itemId) =>
                handleIconClick(itemId, GROUP_ID_AVAILABLE)
              }
            />
          ) : (
            <EmptyFilteredAvailableState
              temItensDisponiveis={availableButtons.length > 0}
              temBuscaAtiva={!!searchTerm}
            />
          )}
        </S.Card>
      </S.QuickButtonsContainer>
    )
  }
)

QuickButtons.displayName = 'QuickButtons'

export default QuickButtons
